import { Button, styled } from '@mui/material';

export interface ShimmerButtonProps {
  /**
   * The opacity of the shimmer effect (0 to 1).
   * Default is 0.4 for most use cases.
   * Use 0.3 for a subtler effect on darker backgrounds.
   */
  shimmerOpacity?: number;
  /**
   * Animation duration in seconds.
   * Default is 2 seconds.
   */
  animationDuration?: number;
}

/**
 * ShimmerButton - A styled MUI Button with an animated shimmer effect.
 *
 * The shimmer creates a glowing sweep animation across the button,
 * useful for drawing attention to CTAs or highlighting trial features.
 *
 * @example
 * // Default shimmer (opacity 0.4)
 * <ShimmerButton variant="contained" color="primary">
 *   Create Your First Note
 * </ShimmerButton>
 *
 * @example
 * // Subtle shimmer for darker backgrounds
 * <ShimmerButton variant="contained" color="primary" shimmerOpacity={0.3}>
 *   Share
 * </ShimmerButton>
 */
const ShimmerButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== 'shimmerOpacity' && prop !== 'animationDuration',
})<ShimmerButtonProps>(({ shimmerOpacity = 0.4, animationDuration = 2 }) => ({
  position: 'relative',
  overflow: 'hidden',

  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: '-100%',
    width: '100%',
    height: '100%',
    background: `linear-gradient(90deg, transparent, rgba(255, 255, 255, ${shimmerOpacity}), transparent)`,
    animation: `shimmer ${animationDuration}s infinite`,
    borderRadius: 'inherit',
  },

  '@keyframes shimmer': {
    '0%': {
      left: '-100%',
    },
    '100%': {
      left: '100%',
    },
  },
}));

export default ShimmerButton;
